import type { Dayjs } from 'dayjs';
import type {
  Appointment,
  AvailableSlot,
  Resource,
  Service,
  ServiceAvailableSlot,
  ServiceFulfillmentMode,
  ServiceRequiredUserInfo,
  ServiceRequirement,
  ServiceResourceLink
} from '@/api/mcp-appointments';
import { buildBookableRange } from '@/components/appointments/slot-scheduling/utils';

/** Resolved required guest/contact flags for a service booking form. */
export interface ResolvedServiceRequiredUserInfo {
  name: boolean;
  email: boolean;
  phone: boolean;
  notes: boolean;
}

/** Primary resource-type requirement for CLIENT_SELECT_PRIMARY services. */
export function getPrimaryRequirement(service: Pick<Service, 'requirements'>): ServiceRequirement | undefined {
  return (service.requirements ?? []).find((r) => r.isPrimaryChoice);
}

/** Active linked resources eligible as the client-selected primary. */
export function getPrimaryResourceCandidates(service: Service): ServiceResourceLink[] {
  const primaryReq = getPrimaryRequirement(service);
  if (!primaryReq) {
    return [];
  }
  const roleId = primaryReq.serviceRoleId;
  return (service.resources ?? []).filter(
    (link) => link.isActive && link.serviceRoleId === roleId
  );
}

export function serviceNeedsPrimaryResource(service: Pick<Service, 'selectionMode'>): boolean {
  return service.selectionMode === 'CLIENT_SELECT_PRIMARY';
}

export function getEffectiveFulfillmentMode(
  service: Pick<Service, 'fulfillmentMode'>
): ServiceFulfillmentMode {
  return service.fulfillmentMode ?? 'APPOINTMENT';
}

/** Active appointment-capable services; callback requests cannot be booked in the calendar. */
export function filterAppointmentServices(services: Service[] | undefined): Service[] {
  return (services ?? []).filter(
    (service) =>
      service.isActive && getEffectiveFulfillmentMode(service) === 'APPOINTMENT'
  );
}

/** Primary resource id for a service appointment (allocation flag or appointment.resourceId). */
export function getServiceAppointmentPrimaryResourceId(appointment: Appointment): string {
  const fromAllocation = appointment.allocatedResources?.find((r) => r.isPrimary)?.resourceId;
  return fromAllocation ?? appointment.resourceId;
}

/**
 * Resource IDs linked to an active service via an active ServiceResource row
 * (matches backend hasActiveServiceForResource / findActiveResourcesForResource).
 */
export function getResourceIdsLinkedToActiveServices(services: Service[] | undefined): Set<string> {
  const ids = new Set<string>();
  if (!services?.length) {
    return ids;
  }
  for (const service of services) {
    if (!service.isActive || getEffectiveFulfillmentMode(service) !== 'APPOINTMENT') {
      continue;
    }
    for (const link of service.resources ?? []) {
      if (link.isActive) {
        ids.add(link.resourceId);
      }
    }
  }
  return ids;
}

/** Resources that can be booked directly (not only through a service). */
export function filterResourcesForDirectBooking(
  resources: Resource[] | undefined,
  services: Service[] | undefined
): Resource[] {
  if (!resources?.length) {
    return [];
  }
  const linkedIds = getResourceIdsLinkedToActiveServices(services);
  return resources.filter((resource) => !linkedIds.has(resource.id));
}

/** Maps service slot API rows to resource-slot picker shape. */
export function mapServiceSlotsToAvailableSlots(
  slots: ServiceAvailableSlot[] | undefined
): AvailableSlot[] {
  if (!slots?.length) {
    return [];
  }
  return slots.map((slot) => ({
    start: slot.start,
    end: slot.end
  }));
}

/** ISO window for a service available-slots query over a visible calendar month. */
export function buildServiceMonthRange(visibleMonth: Dayjs): { startTime: string; endTime: string } | null {
  return buildBookableRange(
    visibleMonth.startOf('month'),
    visibleMonth.endOf('month'),
    undefined
  );
}

/** Guest field requirements resolved from explicit service requiredUserInfo flags. */
export function resolveServiceRequiredUserInfo(
  required: ServiceRequiredUserInfo | null | undefined,
  fulfillmentMode: ServiceFulfillmentMode = 'APPOINTMENT'
): ResolvedServiceRequiredUserInfo {
  if (fulfillmentMode === 'CALLBACK') {
    return {
      name: true,
      email: false,
      phone: true,
      notes: required?.notes ?? false
    };
  }

  return {
    name: required?.name === true,
    email: required?.email === true,
    phone: required?.phone === true,
    notes: required?.notes === true
  };
}

